import { useColorMode, useWindowSize } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { setDayjsLocale } from '@/libs/dayjs'
import i18n from '@/libs/i18n'
import type { MeResponse, SessionState } from '@/types/Session'

const emptySession = (ready = false): SessionState => ({
    ready,
    user:         null,
    roles:        [],
    assignments:  [],
    capabilities: [],
    hasAnyAccess: false,
    panelVersion: '',

    // Enabled, not disabled, and the asymmetry with every other field here is deliberate: the two
    // update fields describe the *panel's deployment*, not the caller, so their zero value is read
    // by screens that render before `/auth/me` has answered. A default of `false` would show
    // "self-update is paused fleet-wide" on the instances list for as long as the bootstrap takes,
    // on every deployment where it is not true (§13.1's default is `true`).
    selfUpdateEnabled:    true,
    expectedAgentVersion: '',
})

export const useAppStore = defineStore('app', () => {
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const loaderActive = ref(0)
    const { width }    = useWindowSize()

    /**
     * Color mode (theme.md) — the store *wraps* the state that already exists, it is not a second
     * source of truth. Nuxt UI's Vue plugin calls VueUse's `useDark()` on the default
     * `vueuse-color-scheme` key, and the pre-paint classic script in `index.html` reads that very
     * key before the bundle runs; naming it here keeps all three on one value. The `.dark` class is
     * VueUse's to write — never toggle it by hand.
     *
     * Two modes only, `'light' | 'dark'` — there is no System option in the UI. Without `emitAuto`
     * the ref always reads the RESOLVED mode, so a first visit (nothing stored yet) shows whatever
     * the OS prefers, and the first click on the topbar's `ColorModeToggle` stores an explicit
     * choice from then on.
     */
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    /**
     * The session model (D76, common contract §3).
     *
     * The session is an opaque id in the `__Host-tpm_session` cookie, which the server sets
     * `HttpOnly; Secure; SameSite=Lax; Path=/` with no `Domain` on the callback 302 and looks up
     * in its own `portal_sessions` store on every request. The SPA never sees it, so there is no
     * token ref, no cookie getter or setter and no token-taking login action here — only what
     * `GET /auth/me` answered. Everything in this state is **non-secret** and hydrated; the
     * cookie rides along on every same-origin request by itself, including `<img src>` and
     * `<a href>`.
     */
    const session      = ref<SessionState>(emptySession())
    /** Resolves when the `/auth/me` bootstrap has settled; the router guard awaits it. */
    const sessionReady = ref<Promise<void> | null>(null)

    /**
     * Whether that one bootstrap call has **finished** — and deliberately *not* the same thing as
     * `session.ready`.
     *
     * `session.ready` says "this session object is a settled answer", and `clearSession()` sets
     * it. Any 401 from anywhere calls `clearSession()` (`stores/fetch.ts`), including one from a
     * request that raced the bootstrap — so `session.ready` turns true while `/auth/me` is still
     * in flight, and anything that read it as "the bootstrap is done" was reading a lie.
     *
     * That lie was the login race: the shell's own requests 401'd first, flipped `ready`, and the
     * bootstrap's own 401 handler then believed it was a *post*-boot 401 and awaited a
     * `router.push` — whose guard was awaiting `sessionReady`, whose promise was awaiting the very
     * fetch whose error handler was doing the pushing. Circular wait: `/auth/me` never settled,
     * the first navigation never finished, and the browser sat on the app shell with no data and
     * no redirect. Only this flag answers "has the bootstrap finished", and only `loadSession()`
     * sets it.
     */
    const sessionSettled = ref(false)

    const isLoggedIn       = () => session.value.ready && session.value.user !== null
    const isSessionSettled = () => sessionSettled.value
    const hasAnyAccess   = () => session.value.hasAnyAccess
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => width.value < 1024

    /**
     * What is actually painted, as a boolean. Only the two CodeMirror surfaces ask: they style
     * themselves from an injected stylesheet a `dark:` utility cannot reach (§9.3), so they need
     * the boolean rather than a class.
     */
    const isDark = computed<boolean>(() => colorMode.value === 'dark')

    const setSession = (me: MeResponse) => {
        session.value = {
            ready:        true,
            user:         me.user ?? null,
            roles:        me.roles ?? [],
            assignments:  me.assignments ?? [],
            capabilities: me.capabilities ?? [],
            hasAnyAccess: me.has_any_access ?? false,
            panelVersion: me.panel_version ?? '',

            // `?? true` rather than `!!`: a panel that does not serve the field is not a panel
            // whose fleet is frozen, and the banner it would raise is one no operator can clear.
            selfUpdateEnabled:    me.self_update_enabled ?? true,
            expectedAgentVersion: me.expected_agent_version ?? '',
        }
    }

    /**
     * Signed out — locally only, and only the non-secret hydrated state: there is no token to
     * discard. The `__Host-tpm_session` cookie is the server's to revoke and clear
     * (`POST /auth/logout`); a 401 from anywhere calls this too, and the page that follows
     * waits for the Sign in button rather than starting OIDC on its own.
     */
    const clearSession = () => {
        session.value = emptySession(true)
    }

    const setSessionReady = (promise: Promise<void>) => {
        sessionReady.value   = promise
        sessionSettled.value = false
    }

    /** Called by `loadSession()` when the bootstrap has answered — or failed. Nothing else. */
    const markSessionSettled = () => {
        sessionSettled.value = true
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        i18n.global.locale.value = newLocale as 'sl' | 'en'

        // Day.js has its own locale register and does not watch vue-i18n's. Without this line a
        // switch to Slovenian leaves every relative time — the issues segment's "for 3 hours", the
        // one place the panel uses `fromNow` — in English (§9.5, W11.6).
        setDayjsLocale(newLocale)
    }

    return {
        locale, session, sessionReady, sessionSettled, loaderActive,
        colorMode, isDark,
        isLoggedIn, isSessionSettled, hasAnyAccess, isLoaderActive, isMobile,
        setSession, clearSession, setSessionReady, markSessionSettled,
        startLoading, stopLoading, changeLocale,
    }
})
